import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  Card, Row, Col, Descriptions, Button, Space, Typography, Tag, Table,
  Spin, Divider, Switch, Tooltip, Modal, Form, InputNumber, Input,
  Select, Progress, message, Badge, Steps,
} from "antd";
import {
  ArrowLeftOutlined, LockOutlined, UnlockOutlined, ClusterOutlined,
  CheckCircleOutlined, FileTextOutlined, ShoppingCartOutlined,
  ArrowRightOutlined, ExclamationCircleOutlined, PrinterOutlined,
} from "@ant-design/icons";
import MachineInstructions from "./MachineInstructions";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getBatchById, moveBatchStage, setBatchLock,
  generateBatchInstructions, saveBatchInstructions,
  getSavedInstructions, getBatchMRP,
} from "../../api/batchPlanningApi";
import { getDefaultFlow } from "../../api/productionApi";
import BatchInstructionsPanel from "./BatchInstructionsPanel";
import { FulfillmentBadge } from "../mrp";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { Title, Text } = Typography;
const { TextArea } = Input;

const priorityColors = { urgent: "red", high: "orange", normal: "blue", low: "default" };

const PRODUCTION_STAGES = [
  "corrugation", "sheet_cutting", "pasting", "casting",
  "pre_print", "coating", "die_cutting", "joining", "after_box_print", "qc",
];

const BatchDetail = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [instructionsData, setInstructionsData] = useState(null);
  const [instructionsLoading, setInstructionsLoading] = useState(false);
  const [mrpData, setMrpData] = useState(null);
  const [mrpLoading, setMrpLoading] = useState(false);
  const [machineInstOpen, setMachineInstOpen] = useState(false);

  // Stage move modal
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [targetStage, setTargetStage] = useState(null);
  const [stageForm] = Form.useForm();

  const fetchBatch = useCallback(async () => {
    setLoading(true);
    try {
      const [batchData, flow] = await Promise.all([getBatchById(id), getDefaultFlow()]);
      setBatch(batchData);
      setStages((flow.stages || []).sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)));
    } catch (err) {
      toast.error("Failed to load batch details");
      navigate("/production/batch-kanban");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchBatch(); }, [fetchBatch]);

  if (!isLogged) return <Navigate to="/auth/login" replace />;
  checkTokenExp(accessToken);

  const handleToggleLock = async () => {
    setActionLoading(true);
    try {
      await setBatchLock(id, !batch.is_locked);
      message.success(batch.is_locked ? "Batch unlocked — jobs can now move independently" : "Batch locked");
      fetchBatch();
    } catch (err) {
      message.error("Failed to toggle lock");
    } finally {
      setActionLoading(false);
    }
  };

  const openMoveModal = (stage) => {
    setTargetStage(stage);
    stageForm.resetFields();
    stageForm.setFieldsValue({ output_qty: batch?.total_quantity || 0 });
    setMoveModalVisible(true);
  };

  const handleMoveStage = async () => {
    if (!targetStage) return;
    try {
      const values = await stageForm.validateFields();
      setActionLoading(true);
      const completion = PRODUCTION_STAGES.includes(batch.current_stage)
        ? { output_qty: values.output_qty, wastage_qty: values.wastage_qty || 0, operator_name: values.operator_name, shift: values.shift, notes: values.notes }
        : null;
      await moveBatchStage(id, targetStage, completion);
      toast.success(`Batch moved to ${stages.find((s) => s.code === targetStage)?.name || targetStage}`);
      setMoveModalVisible(false);
      fetchBatch();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err.response?.data?.error || "Failed to move batch");
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateInstructions = async () => {
    setInstructionsLoading(true);
    try {
      const result = await generateBatchInstructions(id);
      setInstructionsData({ instructions: result, saved: false });
    } catch (err) {
      message.error("Failed to generate instructions");
    } finally {
      setInstructionsLoading(false);
    }
  };

  const handleSaveInstructions = async () => {
    setInstructionsLoading(true);
    try {
      const result = await saveBatchInstructions(id);
      setInstructionsData({ instructions: result, saved: true });
      message.success("Instructions saved");
      fetchBatch();
    } catch (err) {
      message.error("Failed to save instructions");
    } finally {
      setInstructionsLoading(false);
    }
  };

  const handleViewSavedInstructions = async () => {
    setInstructionsLoading(true);
    try {
      const result = await getSavedInstructions(id);
      if (result.length > 0) {
        setInstructionsData({ instructions: result, saved: true });
      } else {
        handleGenerateInstructions();
        return;
      }
    } catch (err) {
      message.error("Failed to load instructions");
    } finally {
      setInstructionsLoading(false);
    }
  };

  const handleLoadMRP = async () => {
    setMrpLoading(true);
    try {
      const result = await getBatchMRP(id);
      setMrpData(result);
    } catch (err) {
      message.error("Failed to load batch MRP");
    } finally {
      setMrpLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 64 }}><Spin size="large" /></div>;
  if (!batch) return null;

  const currentStageIdx = stages.findIndex((s) => s.code === batch.current_stage);
  const nextStage = stages[currentStageIdx + 1] || null;

  const statusColors = { created: "blue", scheduled: "purple", in_progress: "orange", completed: "green", cancelled: "red" };

  const jobColumns = [
    {
      title: "Job #",
      dataIndex: ["job", "job_number"],
      width: 110,
      render: (v, rec) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/job/${rec.job.id}`)}>
          {v}
        </Button>
      ),
    },
    { title: "Customer", dataIndex: ["job", "order", "customer", "name"], width: 140, ellipsis: true },
    { title: "Item", dataIndex: ["job", "item_name"], width: 180, ellipsis: true },
    {
      title: "RS / CS", width: 100,
      render: (_, rec) => (
        <Space size={2}>
          <Tag style={{ fontSize: 10 }}>{rec.job.roll_size}"</Tag>
          {rec.job.cutting_size && <Tag style={{ fontSize: 10 }}>{rec.job.cutting_size}"</Tag>}
        </Space>
      ),
    },
    {
      title: "Qty", width: 90,
      render: (_, rec) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}>{rec.job.completed_quantity} / {rec.job.production_quantity}</Text>
          <Progress
            percent={rec.job.production_quantity > 0
              ? Math.round((rec.job.completed_quantity / rec.job.production_quantity) * 100) : 0}
            size="small" showInfo={false}
          />
        </Space>
      ),
    },
    {
      title: "Priority", dataIndex: ["job", "priority"], width: 80,
      render: (v) => <Tag color={priorityColors[v]}>{v?.toUpperCase()}</Tag>,
    },
    {
      title: "Material", dataIndex: ["job", "fulfillment_status"], width: 120,
      render: (v) => v ? <FulfillmentBadge status={v} /> : "-",
    },
    {
      title: "Stage", dataIndex: ["job", "current_stage"], width: 130,
      render: (v) => <Tag color={v === batch.current_stage ? "blue" : "default"}>{v?.replace(/_/g, " ")}</Tag>,
    },
  ];

  const applicableStages = batch.applicable_stages?.length > 0
    ? stages.filter((s) => batch.applicable_stages.includes(s.code))
    : stages.filter((s) => !["order_received"].includes(s.code));

  return (
    <div>
      <PageTitle title="Back" subtitle={`Batch ${batch.batch_number}`} />

      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle" wrap>
          <Col>
            <Space wrap>
              <ClusterOutlined style={{ fontSize: 20, color: "#1890ff" }} />
              <Title level={4} style={{ margin: 0 }}>{batch.batch_number}</Title>
              <Tag color={statusColors[batch.status]}>{batch.status?.replace(/_/g, " ").toUpperCase()}</Tag>
              <Tag color="blue">RS {batch.roll_size}"</Tag>
              {batch.flute_type && <Tag color="geekblue">{batch.flute_type} Flute</Tag>}
              <Tooltip title={batch.is_locked ? "Jobs move together with the batch. Click to unlock for independent movement." : "Jobs can move independently. Click to lock."}>
                <Switch
                  checkedChildren={<><LockOutlined /> Locked</>}
                  unCheckedChildren={<><UnlockOutlined /> Unlocked</>}
                  checked={batch.is_locked}
                  onChange={handleToggleLock}
                  loading={actionLoading}
                />
              </Tooltip>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {nextStage && batch.status !== "completed" && batch.status !== "cancelled" && (
                <Button
                  type="primary"
                  icon={<ArrowRightOutlined />}
                  onClick={() => openMoveModal(nextStage.code)}
                  loading={actionLoading}
                >
                  Advance to {nextStage.name}
                </Button>
              )}
              <Button
                icon={batch.instructions_saved ? <CheckCircleOutlined /> : <FileTextOutlined />}
                onClick={batch.instructions_saved ? handleViewSavedInstructions : handleGenerateInstructions}
                loading={instructionsLoading}
                style={batch.instructions_saved ? { borderColor: "#52c41a", color: "#52c41a" } : {}}
              >
                {batch.instructions_saved ? "View Instructions" : "Generate Instructions"}
              </Button>
              <Button icon={<ShoppingCartOutlined />} onClick={handleLoadMRP} loading={mrpLoading}>
                Batch MRP
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => setMachineInstOpen(true)}>
                Machine Instructions
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col xs={24} lg={16}>
          {/* Stage Progress */}
          <Card title="Stage Progress" style={{ marginBottom: 16 }}>
            <Steps
              size="small"
              current={currentStageIdx >= 0 ? currentStageIdx : 0}
              status={batch.status === "completed" ? "finish" : batch.status === "cancelled" ? "error" : "process"}
              items={applicableStages.map((s) => ({
                title: s.name,
                description: s.machine_type || undefined,
              }))}
              style={{ overflowX: "auto" }}
            />
            {batch.status !== "completed" && batch.status !== "cancelled" && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Move batch to stage: </Text>
                <Space wrap style={{ marginTop: 4 }}>
                  {applicableStages.map((s) => (
                    <Button
                      key={s.code}
                      size="small"
                      type={s.code === batch.current_stage ? "primary" : "default"}
                      disabled={s.code === batch.current_stage}
                      onClick={() => openMoveModal(s.code)}
                    >
                      {s.name}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </Card>

          {/* Jobs Table */}
          <Card
            title={
              <Space>
                <span>Jobs in Batch</span>
                <Badge count={batch.jobs?.length} style={{ backgroundColor: "#1890ff" }} />
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Table
              dataSource={batch.jobs || []}
              columns={jobColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 900 }}
            />
          </Card>

          {/* Instructions */}
          {instructionsData && (
            <Card
              title="Work Instructions"
              style={{ marginBottom: 16 }}
              extra={
                !instructionsData.saved && (
                  <Button type="primary" onClick={handleSaveInstructions} loading={instructionsLoading}>
                    Save Instructions
                  </Button>
                )
              }
            >
              <BatchInstructionsPanel instructions={instructionsData.instructions} />
            </Card>
          )}

          {/* MRP */}
          {mrpData && (
            <Card title="Material Requirements (Batch MRP)" style={{ marginBottom: 16 }}>
              <Table
                size="small"
                dataSource={mrpData.materials || []}
                rowKey={(r) => r.material_id || r.material_name}
                pagination={false}
                columns={[
                  { title: "Material", dataIndex: "material_name", ellipsis: true },
                  { title: "Stage", dataIndex: "stage", width: 120, render: (v) => v?.replace(/_/g, " ") },
                  { title: "Required", dataIndex: "required_qty", width: 100, render: (v, r) => `${v} ${r.unit || "kg"}` },
                  { title: "Available", dataIndex: "available_qty", width: 100, render: (v, r) => `${v} ${r.unit || "kg"}` },
                  {
                    title: "Status", dataIndex: "status", width: 100,
                    render: (v) => <Tag color={v === "fulfilled" ? "green" : v === "partial" ? "orange" : "red"}>{v}</Tag>,
                  },
                ]}
              />
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          {/* Batch Info */}
          <Card title="Batch Info" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Batch Number">{batch.batch_number}</Descriptions.Item>
              <Descriptions.Item label="Roll Size">{batch.roll_size}"</Descriptions.Item>
              {batch.cutting_size && <Descriptions.Item label="Cut Size">{batch.cutting_size}"</Descriptions.Item>}
              {batch.flute_type && <Descriptions.Item label="Flute">{batch.flute_type}</Descriptions.Item>}
              <Descriptions.Item label="Total Qty">{batch.total_quantity?.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Jobs">{batch.jobs?.length}</Descriptions.Item>
              <Descriptions.Item label="Current Stage">
                <Tag color="blue">{batch.current_stage?.replace(/_/g, " ")}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Lock">
                {batch.is_locked
                  ? <Tag icon={<LockOutlined />} color="default">Locked</Tag>
                  : <Tag icon={<UnlockOutlined />} color="warning">Unlocked</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Instructions">
                {batch.instructions_saved
                  ? <Tag color="green">Saved</Tag>
                  : <Tag color="default">Not Generated</Tag>}
              </Descriptions.Item>
              {batch.earliest_delivery && (
                <Descriptions.Item label="Earliest Delivery">
                  {moment(batch.earliest_delivery).format("DD MMM YYYY")}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Created">
                {moment(batch.created_at).format("DD MMM YYYY, HH:mm")}
              </Descriptions.Item>
              {batch.notes && <Descriptions.Item label="Notes">{batch.notes}</Descriptions.Item>}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Move Stage Modal */}
      <Modal
        title={
          <Space>
            <ArrowRightOutlined />
            <span>Move Batch to {stages.find((s) => s.code === targetStage)?.name}</span>
          </Space>
        }
        open={moveModalVisible}
        onOk={handleMoveStage}
        onCancel={() => { setMoveModalVisible(false); setTargetStage(null); }}
        okText="Confirm Move"
        confirmLoading={actionLoading}
        width={480}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          All <Text strong>{batch.jobs?.length} jobs</Text> in this batch will be moved to{" "}
          <Tag color="blue">{stages.find((s) => s.code === targetStage)?.name}</Tag>.
        </Text>
        {batch.current_stage && PRODUCTION_STAGES.includes(batch.current_stage) && (
          <>
            <Divider style={{ margin: "8px 0" }}>Stage Completion Data</Divider>
            <Form form={stageForm} layout="vertical" size="small">
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Output Qty" name="output_qty" rules={[{ required: true }]}>
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Wastage Qty" name="wastage_qty">
                    <InputNumber style={{ width: "100%" }} min={0} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Operator" name="operator_name">
                    <Input placeholder="Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Shift" name="shift">
                    <Select placeholder="Shift" allowClear>
                      <Select.Option value="Morning">Morning</Select.Option>
                      <Select.Option value="Evening">Evening</Select.Option>
                      <Select.Option value="Night">Night</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Notes" name="notes">
                <TextArea rows={2} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Machine / Stage Instructions */}
      <MachineInstructions open={machineInstOpen} onClose={() => setMachineInstOpen(false)} />
    </div>
  );
};

export default BatchDetail;
